import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { GitMerge, Users, Link2, Unlink, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function MemberLine({ r, onUnlink }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#E2E8F0] bg-[#F8F9FA] px-3 py-2" data-testid={`pf-member-${r.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] text-[#6B7F82]">{r.reference_code}</span>
          <span className="font-body text-sm text-[#1A2024]">{r.full_name}</span>
        </div>
        <div className="font-body text-xs text-[#5C6873]">
          Reported by <span className="font-medium text-[#1A2024]">{r.contributor_name}</span>
          {(r.age_min != null) && <> · age {r.age_min}</>}
          {r.event_date && <> · {r.event_date}</>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/ante-mortem/${r.id}`} data-testid={`pf-view-${r.id}`}>
          <Button variant="ghost" size="sm" className="text-[#2E3A46]"><ExternalLink size={13} className="mr-1" /> View</Button>
        </Link>
        {onUnlink && (
          <Button variant="ghost" size="sm" className="text-[#9E4747] hover:bg-[#9E4747]/10" onClick={() => onUnlink(r.id)} data-testid={`pf-unlink-${r.id}`}>
            <Unlink size={13} className="mr-1" /> Unlink
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminPersonFilesPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        api.get("/admin/duplicate-suggestions"),
        api.get("/admin/person-files"),
      ]);
      setSuggestions(s.data);
      setFiles(f.data);
    } catch {
      toast.error("Could not load person files");
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const linkCluster = async (records, groupId = null) => {
    try {
      await api.post("/admin/person-files/link", { record_ids: records.map((r) => r.id), group_id: groupId });
      toast.success("Records linked into one person file");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not link records");
    }
  };

  const unlink = async (recordId) => {
    try {
      await api.post("/admin/person-files/unlink", { record_id: recordId });
      toast.success("Record unlinked");
      refresh();
    } catch {
      toast.error("Could not unlink");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="admin-person-files-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · records</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white"><GitMerge size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Person files</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        When several families report the same missing person, link their reports into one combined file.
        Each family's original report is preserved and stays editable only by them — nothing is overwritten.
      </p>

      {loading && <p className="mt-8 font-body text-sm text-[#5C6873]" data-testid="pf-loading">Loading…</p>}

      {!loading && (
        <>
          {/* Suggestions */}
          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-[#1A2024]">
              <AlertCircle size={16} className="text-[#C99B4B]" /> Suggested duplicates
            </h2>
            <p className="mt-1 font-body text-sm text-[#5C6873]">Unlinked reports that share the same name and country.</p>
            <div className="mt-4 space-y-4" data-testid="pf-suggestions">
              {suggestions.length === 0 && (
                <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-6 text-center font-body text-sm text-[#5C6873]" data-testid="pf-suggestions-empty">
                  No potential duplicates right now.
                </div>
              )}
              {suggestions.map((c, i) => (
                <div key={i} className="rounded-lg border border-[#C99B4B]/30 bg-[#C99B4B]/5 p-4 shadow-sm" data-testid={`pf-suggestion-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-heading text-base font-semibold text-[#1A2024]">
                      {c.full_name} <Badge variant="outline" className="ml-1 rounded-full border-[#E2E8F0] text-[11px]">{c.country}</Badge>
                      <span className="ml-2 font-body text-xs font-normal text-[#5C6873]">{c.records.length} reports</span>
                    </div>
                    <Button size="sm" className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" onClick={() => linkCluster(c.records)} data-testid={`pf-link-${i}`}>
                      <Link2 size={14} className="mr-1" /> Link into one file
                    </Button>
                  </div>
                  <div className="mt-3 space-y-2">
                    {c.records.map((r) => <MemberLine key={r.id} r={r} />)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Existing files */}
          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-[#1A2024]">
              <Users size={16} className="text-[#4A7C59]" /> Linked person files
            </h2>
            <div className="mt-4 space-y-4" data-testid="pf-files">
              {files.length === 0 && (
                <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-6 text-center font-body text-sm text-[#5C6873]" data-testid="pf-files-empty">
                  No person files yet. Link some reports above to create one.
                </div>
              )}
              {files.map((f) => (
                <div key={f.group_id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`pf-file-${f.group_id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-heading text-base font-semibold text-[#1A2024]">{f.display_name}</span>
                    <Badge variant="outline" className="rounded-full border-[#E2E8F0] text-[11px]">{f.country}</Badge>
                    <Badge className="rounded-full bg-[#4A7C59]/10 text-[#4A7C59] hover:bg-[#4A7C59]/15">{f.member_count} contributions</Badge>
                  </div>
                  <div className="mt-3 space-y-2">
                    {f.members.map((r) => <MemberLine key={r.id} r={r} onUnlink={unlink} />)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
